import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Form, 
  Button, 
  Row, 
  Col, 
  Tab, 
  Nav,
  Card,
  Badge
} from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FiX, 
  FiSave, 
  FiMonitor, 
  FiBell, 
  FiGlobe, 
  FiShield, 
  FiLayout,
  FiEye,
  FiSettings
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import './PreferencesModal.scss';

const PreferencesModal = ({ show, onHide }) => {
  const [activeTab, setActiveTab] = useState('appearance');
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    // Appearance preferences
    theme: 'auto',
    language: 'en',
    fontSize: 'medium',
    colorScheme: 'blue',
    layoutDensity: 'comfortable',
    reducedMotion: false,
    highContrast: false,
    
    // Notification preferences
    emailNotifications: {
      assignments: true,
      exams: true,
      announcements: true,
      grades: true,
      attendance: false,
      maintenance: true
    },
    pushNotifications: true,
    notificationSound: true,
    notificationFrequency: 'realtime',
    
    // Academic preferences
    defaultAcademicYear: 'current',
    timeZone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12-hour',
    gradeDisplay: 'percentage',
    
    // Dashboard preferences
    widgetPreferences: {
      quickStats: true,
      recentActivities: true,
      upcomingEvents: true,
      notifications: true,
      calendar: true,
      todoList: false
    },
    defaultLandingPage: 'dashboard',
    itemsPerPage: 25,
    
    // Privacy preferences
    profileVisibility: 'institute',
    showOnlineStatus: true,
    allowDataSharing: false,
    twoFactorAuth: false,
    autoLogout: 30,
    
    // Communication preferences
    chatOnlineStatus: true,
    chatReadReceipts: true,
    emailDigest: 'daily',
    allowMessages: 'everyone'
  });

  // Load preferences from localStorage or API on component mount
  useEffect(() => {
    if (show) {
      loadPreferences();
    }
  }, [show]);

  const loadPreferences = () => {
    try {
      const savedPreferences = localStorage.getItem('proton_preferences');
      if (savedPreferences) {
        setPreferences(prev => ({
          ...prev,
          ...JSON.parse(savedPreferences)
        }));
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const handlePreferenceChange = (category, key, value) => {
    if (category) {
      setPreferences(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      }));
    } else {
      setPreferences(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Save to localStorage (in a real app, you'd save to your API)
      localStorage.setItem('proton_preferences', JSON.stringify(preferences));
      
      // Apply theme changes immediately
      applyThemeChanges();
      
      toast.success('Preferences saved successfully');
      onHide();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const applyThemeChanges = () => {
    const root = document.documentElement;
    
    // Apply theme
    if (preferences.theme === 'dark') {
      root.classList.add('dark-theme');
    } else if (preferences.theme === 'light') {
      root.classList.remove('dark-theme');
    } else {
      // Auto theme based on system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark-theme');
      } else {
        root.classList.remove('dark-theme');
      }
    }
    
    // Apply font size
    root.style.setProperty('--base-font-size', {
      'small': '14px',
      'medium': '16px',
      'large': '18px',
      'extra-large': '20px'
    }[preferences.fontSize]);
    
    // Apply reduced motion
    if (preferences.reducedMotion) {
      root.style.setProperty('--motion-duration', '0s');
    } else {
      root.style.setProperty('--motion-duration', '0.3s');
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all preferences to default values?')) {
      setPreferences({
        theme: 'auto',
        language: 'en',
        fontSize: 'medium',
        colorScheme: 'blue',
        layoutDensity: 'comfortable',
        reducedMotion: false,
        highContrast: false,
        emailNotifications: {
          assignments: true,
          exams: true,
          announcements: true,
          grades: true,
          attendance: false,
          maintenance: true
        },
        pushNotifications: true,
        notificationSound: true,
        notificationFrequency: 'realtime',
        defaultAcademicYear: 'current',
        timeZone: 'Asia/Kolkata',
        dateFormat: 'DD/MM/YYYY',
        timeFormat: '12-hour',
        gradeDisplay: 'percentage',
        widgetPreferences: {
          quickStats: true,
          recentActivities: true,
          upcomingEvents: true,
          notifications: true,
          calendar: true,
          todoList: false
        },
        defaultLandingPage: 'dashboard',
        itemsPerPage: 25,
        profileVisibility: 'institute',
        showOnlineStatus: true,
        allowDataSharing: false,
        twoFactorAuth: false,
        autoLogout: 30,
        chatOnlineStatus: true,
        chatReadReceipts: true,
        emailDigest: 'daily',
        allowMessages: 'everyone'
      });
      toast.success('Preferences reset to defaults');
    }
  };

  const renderAppearanceTab = () => (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label className="d-flex align-items-center mb-3">
            <FiMonitor className="me-2" />
            <strong>Theme</strong>
          </Form.Label>
          <div className="theme-options">
            {[
              { value: 'light', label: 'Light', icon: '☀️' },
              { value: 'dark', label: 'Dark', icon: '🌙' },
              { value: 'auto', label: 'Auto', icon: '🔄' }
            ].map(option => (
              <label key={option.value} className="theme-option">
                <input
                  type="radio"
                  name="theme"
                  value={option.value}
                  checked={preferences.theme === option.value}
                  onChange={(e) => handlePreferenceChange(null, 'theme', e.target.value)}
                />
                <div className="theme-card">
                  <span className="theme-icon">{option.icon}</span>
                  <span className="theme-label">{option.label}</span>
                </div>
              </label>
            ))}
          </div>
        </Form.Group>
      </Col>
      
      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label className="d-flex align-items-center mb-3">
            <FiGlobe className="me-2" />
            <strong>Language</strong>
          </Form.Label>
          <Form.Select
            value={preferences.language}
            onChange={(e) => handlePreferenceChange(null, 'language', e.target.value)}
          >
            <option value="en">English</option>
            <option value="hi">हिन्दी (Hindi)</option>
            <option value="ta">தமிழ் (Tamil)</option>
            <option value="te">తెలుగు (Telugu)</option>
            <option value="kn">ಕನ್ನಡ (Kannada)</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Font Size</strong></Form.Label>
          <Form.Select
            value={preferences.fontSize}
            onChange={(e) => handlePreferenceChange(null, 'fontSize', e.target.value)}
          >
            <option value="small">Small (14px)</option>
            <option value="medium">Medium (16px)</option>
            <option value="large">Large (18px)</option>
            <option value="extra-large">Extra Large (20px)</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Layout Density</strong></Form.Label>
          <Form.Select
            value={preferences.layoutDensity}
            onChange={(e) => handlePreferenceChange(null, 'layoutDensity', e.target.value)}
          >
            <option value="compact">Compact</option>
            <option value="comfortable">Comfortable</option>
            <option value="spacious">Spacious</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={12}>
        <div className="accessibility-options">
          <h6 className="mb-3">Accessibility Options</h6>
          <Form.Check
            type="switch"
            label="Reduce Motion"
            checked={preferences.reducedMotion}
            onChange={(e) => handlePreferenceChange(null, 'reducedMotion', e.target.checked)}
            className="mb-2"
          />
          <Form.Check
            type="switch"
            label="High Contrast Mode"
            checked={preferences.highContrast}
            onChange={(e) => handlePreferenceChange(null, 'highContrast', e.target.checked)}
          />
        </div>
      </Col>
    </Row>
  );

  const renderNotificationsTab = () => (
    <Row>
      <Col md={6}>
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0 d-flex align-items-center">
              <FiBell className="me-2" />
              Email Notifications
            </h6>
          </Card.Header>
          <Card.Body>
            {Object.entries(preferences.emailNotifications).map(([key, value]) => (
              <Form.Check
                key={key}
                type="switch"
                label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                checked={value}
                onChange={(e) => handlePreferenceChange('emailNotifications', key, e.target.checked)}
                className="mb-2"
              />
            ))}
          </Card.Body>
        </Card>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Notification Frequency</strong></Form.Label>
          <Form.Select
            value={preferences.notificationFrequency}
            onChange={(e) => handlePreferenceChange(null, 'notificationFrequency', e.target.value)}
          >
            <option value="realtime">Real-time</option>
            <option value="daily">Daily Digest</option>
            <option value="weekly">Weekly Summary</option>
            <option value="off">Turn Off</option>
          </Form.Select>
        </Form.Group>

        <Form.Check
          type="switch"
          label="Enable Push Notifications"
          checked={preferences.pushNotifications}
          onChange={(e) => handlePreferenceChange(null, 'pushNotifications', e.target.checked)}
          className="mb-3"
        />

        <Form.Check
          type="switch"
          label="Notification Sounds"
          checked={preferences.notificationSound}
          onChange={(e) => handlePreferenceChange(null, 'notificationSound', e.target.checked)}
        />
      </Col>
    </Row>
  );

  const renderAcademicTab = () => (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Date Format</strong></Form.Label>
          <Form.Select
            value={preferences.dateFormat}
            onChange={(e) => handlePreferenceChange(null, 'dateFormat', e.target.value)}
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Time Format</strong></Form.Label>
          <Form.Select
            value={preferences.timeFormat}
            onChange={(e) => handlePreferenceChange(null, 'timeFormat', e.target.value)}
          >
            <option value="12-hour">12 Hour (AM/PM)</option>
            <option value="24-hour">24 Hour</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Grade Display</strong></Form.Label>
          <Form.Select
            value={preferences.gradeDisplay}
            onChange={(e) => handlePreferenceChange(null, 'gradeDisplay', e.target.value)}
          >
            <option value="percentage">Percentage (%)</option>
            <option value="gpa">GPA Points</option>
            <option value="letter">Letter Grades</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Time Zone</strong></Form.Label>
          <Form.Select
            value={preferences.timeZone}
            onChange={(e) => handlePreferenceChange(null, 'timeZone', e.target.value)}
          >
            <option value="Asia/Kolkata">India Standard Time (IST)</option>
            <option value="Asia/Dubai">Gulf Standard Time (GST)</option>
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="Europe/London">Greenwich Mean Time (GMT)</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </Row>
  );

  const renderDashboardTab = () => (
    <Row>
      <Col md={12}>
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0 d-flex align-items-center">
              <FiLayout className="me-2" />
              Dashboard Widgets
            </h6>
          </Card.Header>
          <Card.Body>
            <Row>
              {Object.entries(preferences.widgetPreferences).map(([key, value]) => (
                <Col md={6} key={key} className="mb-2">
                  <Form.Check
                    type="switch"
                    label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                    checked={value}
                    onChange={(e) => handlePreferenceChange('widgetPreferences', key, e.target.checked)}
                  />
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Default Landing Page</strong></Form.Label>
          <Form.Select
            value={preferences.defaultLandingPage}
            onChange={(e) => handlePreferenceChange(null, 'defaultLandingPage', e.target.value)}
          >
            <option value="dashboard">Dashboard</option>
            <option value="calendar">Calendar</option>
            <option value="classes">Classes</option>
            <option value="activities">Recent Activities</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Items Per Page</strong></Form.Label>
          <Form.Select
            value={preferences.itemsPerPage}
            onChange={(e) => handlePreferenceChange(null, 'itemsPerPage', parseInt(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </Row>
  );

  const renderPrivacyTab = () => (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Profile Visibility</strong></Form.Label>
          <Form.Select
            value={preferences.profileVisibility}
            onChange={(e) => handlePreferenceChange(null, 'profileVisibility', e.target.value)}
          >
            <option value="public">Public</option>
            <option value="institute">Institute Only</option>
            <option value="private">Private</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label><strong>Auto Logout (minutes)</strong></Form.Label>
          <Form.Select
            value={preferences.autoLogout}
            onChange={(e) => handlePreferenceChange(null, 'autoLogout', parseInt(e.target.value))}
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
            <option value={120}>2 hours</option>
            <option value={0}>Never</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={12}>
        <div className="privacy-options">
          <Form.Check
            type="switch"
            label="Show Online Status to Others"
            checked={preferences.showOnlineStatus}
            onChange={(e) => handlePreferenceChange(null, 'showOnlineStatus', e.target.checked)}
            className="mb-3"
          />
          
          <Form.Check
            type="switch"
            label="Allow Analytics Data Sharing"
            checked={preferences.allowDataSharing}
            onChange={(e) => handlePreferenceChange(null, 'allowDataSharing', e.target.checked)}
            className="mb-3"
          />

          <div className="two-factor-section">
            <Form.Check
              type="switch"
              label="Enable Two-Factor Authentication"
              checked={preferences.twoFactorAuth}
              onChange={(e) => handlePreferenceChange(null, 'twoFactorAuth', e.target.checked)}
              className="mb-2"
            />
            {preferences.twoFactorAuth && (
              <Badge bg="success" className="ms-3">
                <FiShield className="me-1" />
                Enhanced Security Active
              </Badge>
            )}
          </div>
        </div>
      </Col>
    </Row>
  );

  if (!show) return null;

  const modalContent = (
    <motion.div
      className="preferences-modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onHide}
    >
      <motion.div
        className="preferences-modal"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="preferences-header">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <FiSettings className="me-2" />
              Preferences
            </h4>
            <p className="text-muted mb-0">Customize your Proton experience</p>
          </div>
          <Button variant="link" className="close-btn" onClick={onHide}>
            <FiX size={20} />
          </Button>
        </div>

        {/* Modal Body */}
        <div className="preferences-body">
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <div className="preferences-nav">
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link eventKey="appearance">
                    <FiEye className="me-2" />
                    Appearance
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="notifications">
                    <FiBell className="me-2" />
                    Notifications
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="academic">
                    <FiGlobe className="me-2" />
                    Academic
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="dashboard">
                    <FiLayout className="me-2" />
                    Dashboard
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="privacy">
                    <FiShield className="me-2" />
                    Privacy & Security
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            <div className="preferences-content">
              <Tab.Content>
                <Tab.Pane eventKey="appearance">
                  {renderAppearanceTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="notifications">
                  {renderNotificationsTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="academic">
                  {renderAcademicTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="dashboard">
                  {renderDashboardTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="privacy">
                  {renderPrivacyTab()}
                </Tab.Pane>
              </Tab.Content>
            </div>
          </Tab.Container>
        </div>

        {/* Modal Footer */}
        <div className="preferences-footer">
          <div className="d-flex justify-content-between align-items-center w-100">
            <Button variant="outline-secondary" onClick={resetToDefaults}>
              Reset to Defaults
            </Button>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={onHide}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={loading}>
                {loading ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="me-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return createPortal(modalContent, document.body);
};

export default PreferencesModal;
